/* eslint-disable @typescript-eslint/no-explicit-any */
import { action, flow, makeAutoObservable, observable } from 'mobx';
import { StandardWalletAdapter } from '@solana/wallet-standard-wallet-adapter-base';
import EVMBase from './EVMBase';
import { BaseWallet } from './BaseWallet';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { SolanaMobileWalletAdapter } from '@solana-mobile/wallet-adapter-mobile';
import { BaseMessageSignerWalletAdapter, WalletReadyState } from '@solana/wallet-adapter-base';

type Adapter = StandardWalletAdapter | SolanaMobileWalletAdapter | BaseMessageSignerWalletAdapter;

export default class SolanaWallet implements BaseWallet {
	readonly type = 'solana';
	adapter: Adapter = undefined as any;

	readonly website: string | undefined = undefined;
	readonly info: string | undefined = undefined;
	readonly chainId = 0;
	readonly supportedChainIds = [0, 1110111];

	publicKey: PublicKey | null = null;
	connecting = false;
	connected = false;
	disconnecting = false;

	get name() {
		return this.adapter?.name || 'SolanaWallet';
	}
	get icon() {
		return this.adapter?.icon || '';
	}
	isEVM(): this is EVMBase {
		return false;
	}

	get isMobileAdapter() {
		return this.adapter instanceof SolanaMobileWalletAdapter;
	}

	constructor(adapter: Adapter) {
		this.adapter = adapter;
		makeAutoObservable(this, { adapter: observable.ref }, { autoBind: true });
		this.adapter.on('connect', this.onConnect);
		this.adapter.on('disconnect', this.onDisconnect);
		this.#checkConnected();
	}

	#checkConnected = action(() => {
		const adapter = this.adapter;
		if (!('wallet' in adapter)) return;
		if (!(adapter as any).wallet?.accounts?.length) return;
		this.adapter.autoConnect();
	});

	get isReady() {
		return Boolean(this.connected && this.selectedAddress);
	}

	get selectedAddress() {
		return this.publicKey?.toString();
	}

	get disconnectable() {
		return this.connected;
	}

	get readyState() {
		return this.adapter.readyState;
	}

	get installed() {
		return [WalletReadyState.Installed, WalletReadyState.Loadable].includes(this.readyState);
	}

	supports(chain: Readonly<Chain> | Chain | number) {
		const chainId = typeof chain !== 'number' ? chain.chainId : chain;
		return this.supportedChainIds.includes(chainId);
	}

	addListeners() {}

	removeListeners() {}

	disconnect = flow(function* disconnect(this: SolanaWallet) {
		this.disconnecting = true;
		try {
			yield this.adapter.disconnect();
		} finally {
			this.disconnecting = false;
		}
	});

	connect = flow(function* connect(this: SolanaWallet) {
		this.connecting = true;
		try {
			yield this.adapter.connect();
		} finally {
			this.connecting = false;
		}
	});

	signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
		if (!this.connected) throw new Error('Wallet not connected');
		if (!('signTransaction' in this.adapter) || !this.adapter.signTransaction) {
			throw new Error(`Wallet doesn't support signTransaction`);
		}
		return this.adapter.signTransaction(transaction);
	}

	signAllTransactions<T extends Transaction | VersionedTransaction>(transaction: T[]): Promise<T[]> {
		if (!this.connected) throw new Error('Wallet not connected');
		if (!('signAllTransactions' in this.adapter) || !this.adapter.signAllTransactions) {
			throw new Error(`Wallet doesn't support signAllTransactions`);
		}
		return this.adapter.signAllTransactions(transaction);
	}
	signMessage(message: Uint8Array): Promise<Uint8Array> {
		if (!this.connected) throw new Error('Wallet not connected');
		if (!('signMessage' in this.adapter) || !this.adapter.signMessage) {
			throw new Error(`Wallet doesn't support signMessage`);
		}
		return this.adapter.signMessage(message);
	}

	private onConnect = action(() => {
		this.publicKey = this.adapter.publicKey;
		this.connected = true;
	});

	private onDisconnect = action(() => {
		this.publicKey = null;
		this.connected = false;
	});
}